import React, { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { StackedColumnCategoryChart, StackedColumnsCategoryChartProps } from '../../../../components/chart/stackedColumnsCategoryChart';
import { ISmartBatteryCycleCountIntervalStats } from '../../../../services/shared/battery/batteryCentricService';
import Card from '@components/card/card';
import '../style.css';
import { createChartTooltipAdapter, getTitle } from './lib';

interface IProps {
  data: ISmartBatteryCycleCountIntervalStats,
  isLoading?: boolean
}

interface BatteryCycleCountChartData {
  bucket: string,
  count: number
}

const series = [
  {
    visible: true,
    dataKey: 'count' as keyof BatteryCycleCountChartData,
    description: 'Batteries Cycle Count',
    colour: '#5b9ad2'
  },
];

export function BatteryCycleCountChartContainer(props: IProps) {
  const ns = 'batteryEssentialsChart';
  const { t } = useTranslation(ns);

  const chartData: BatteryCycleCountChartData[] = useMemo(() => {
    if (!props.data) return [];

    const data = [
      {
        bucket: '0-100',
        count: props.data.cycles0To100,
      },
      {
        bucket: '100-200',
        count: props.data.cycles100To200,
      },
      {
        bucket: '200-300',
        count: props.data.cycles200To300,
      },
      {
        bucket: '300-400',
        count: props.data.cycles300To400,
      },
      {
        bucket: '400-500',
        count: props.data.cycles400To500,
      },
      {
        bucket: '500-600',
        count: props.data.cycles500To600,
      },
      {
        bucket: '600-700',
        count: props.data.cycles600To700,
      },
      {
        bucket: '>700',
        count: props.data.cycles700AndAbove,
      }
    ];
    return data;
  }, [props.data]);

  const chartProps: StackedColumnsCategoryChartProps<BatteryCycleCountChartData> = {
    data: !props.isLoading ? chartData : null,
    series,
    title: getTitle(t, 'BATTERY_CYCLECOUNT_CHART_TITLE'),
    cypressId: 'battery-cyclecount-chart',
    className: 'battery-cyclecount-chart',
    hasLegend: false,
    bottomBorder: false,
    columnWidth: 60,
    yAxisType: 'value',
    useAxisTooltip: true,
    amchartsYAxisNumberFormat: '#.',
    categoryKey: 'bucket',
    drawXAxis: (chart, xAxis) => {
      xAxis.title.text = t('BATTERY_CYCLECOUNT_CHART_LABEL');
      xAxis.renderer.labels.template.fontSize = 12;
      xAxis.renderer.labels.template.horizontalCenter = 'middle';
      xAxis.renderer.labels.template.verticalCenter = 'middle';
      xAxis.renderer.minGridDistance = 20;
      return xAxis;
    },
    drawYAxis: (chart, yAxis) => {
      yAxis.title.text = t('NO_OF_BATTERIES');
      yAxis.title.fontSize = 12;
      yAxis.renderer.labels.template.fontSize = 12;
      yAxis.min = 0;
      yAxis.strictMinMax = false;
      yAxis.renderer.minGridDistance = 30;
      yAxis.maxPrecision = 0;
      return yAxis;
    },
    getAxisTooltipAdapter: () => createChartTooltipAdapter(t)
  };

  return (
    <Card noPadding={false} className="battery-cyclecount-container">
      <StackedColumnCategoryChart<any>
        {...chartProps}
      />
    </Card>
  );
}
